import React, { useMemo, useState } from 'react';
import { Space, Switch, Table, Tag, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { ExpV2Counts, ExpV2Test } from '@/types/experimentsV2';
import { ACTION_LABEL } from './actions';
import { STATUS_COLOR, STATUS_LABEL, srmBadge } from './display';
import { formatPercent } from './units';

interface Props {
  tests: ExpV2Test[];
  /** Live counts by test key; missing for flags, drafts and calls that failed. */
  counts: Record<string, ExpV2Counts | undefined>;
  onOpen: (key: string) => void;
  loading?: boolean;
}

const pendingLabel = (action: string) =>
  ACTION_LABEL[action as keyof typeof ACTION_LABEL] ?? action;

export const TestsTable: React.FC<Props> = ({ tests, counts, onOpen, loading }) => {
  const [showEnded, setShowEnded] = useState(false);
  const rows = useMemo(
    () => (showEnded ? tests : tests.filter((t) => t.status !== 'ended')),
    [tests, showEnded]
  );

  const columns: ColumnsType<ExpV2Test> = [
    {
      title: 'Key',
      dataIndex: 'key',
      render: (key: string) => (
        <a
          href={`/experiments-v2/${key}`}
          onClick={(e) => {
            e.preventDefault();
            onOpen(key);
          }}
        >
          <Typography.Text code>{key}</Typography.Text>
        </a>
      ),
    },
    { title: 'Name', dataIndex: 'name' },
    { title: 'Kind', dataIndex: 'kind', render: (k: string) => (k === 'flag' ? 'Flag' : 'Test') },
    { title: 'Layer', dataIndex: 'layerKey' },
    {
      title: 'Status',
      dataIndex: 'status',
      render: (_: unknown, t) => (
        <Space size={4} wrap>
          <Tag color={STATUS_COLOR[t.status]}>{STATUS_LABEL[t.status]}</Tag>
          {t.economy && <Tag>Economy</Tag>}
          {t.pendingAction && (
            <Tag color="gold">Waiting: {pendingLabel(t.pendingAction.action)}</Tag>
          )}
        </Space>
      ),
    },
    {
      title: 'Live',
      key: 'live',
      render: (_: unknown, t) => `${formatPercent(t.rolloutSize)} of ${formatPercent(t.rangeSize)}`,
    },
    {
      title: 'Players per variant',
      key: 'players',
      render: (_: unknown, t) => {
        const c = counts[t.key];
        if (!c) return <span data-players="none">—</span>;
        return (
          <span data-players="some">
            {c.variants
              .map((v) => `${v.variant} ${v.assigned.toLocaleString('en-IN')}`)
              .join(' · ')}
            {c.variants.length > 1 &&
              ` · total ${c.variants.reduce((n, v) => n + v.assigned, 0).toLocaleString('en-IN')}`}
          </span>
        );
      },
    },
    {
      title: 'Split',
      key: 'split',
      render: (_: unknown, t) => {
        const c = counts[t.key];
        if (!c) return '—';
        const badge = srmBadge(c);
        return <Tag color={badge.color}>{badge.text}</Tag>;
      },
    },
  ];

  return (
    <Space direction="vertical" style={{ width: '100%' }}>
      <Space>
        <Switch id="expv2-show-ended" size="small" checked={showEnded} onChange={setShowEnded} />
        <label htmlFor="expv2-show-ended">Show ended</label>
      </Space>
      <Table<ExpV2Test>
        rowKey="key"
        columns={columns}
        dataSource={rows}
        loading={loading}
        pagination={{ pageSize: 20, hideOnSinglePage: true }}
        scroll={{ x: true }}
      />
    </Space>
  );
};
